import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { TrialExpiredBanner } from '~/features/trial/components/trial-expired-banner';
import { TrialNotStartedMessage } from '~/features/trial/components/trial-not-started-message';
import { useTrialContractStatus } from '~/features/trial/hooks/use-trial-contract-status';
import type { TrialPlatform } from '~/features/trial/types/trial.types';
import { requireContract } from '~/utils/guards';

const VALID_PLATFORMS: TrialPlatform[] = ['google', 'meta'];

/**
 * Renders the temporal state of the platform's trial window: a full-page
 * message if it hasn't started yet, a persistent banner + read-only wizard if
 * it has expired, or the wizard as normal otherwise. Kept separate from
 * `beforeLoad` since "not started" is an inline message, not a redirect.
 */
function TrialPlatformLayout() {
  const { platform } = Route.useParams();
  const { status, pocStartDate } = useTrialContractStatus(platform as TrialPlatform);

  if (status === 'loading') return <PageSkeleton />;
  if (status === 'not_started') {
    return (
      <TrialNotStartedMessage platform={platform as TrialPlatform} pocStartDate={pocStartDate} />
    );
  }

  return (
    <>
      {status === 'expired' && <TrialExpiredBanner />}
      <Outlet />
    </>
  );
}

export const Route = createFileRoute('/_authenticated/_tenant/trial/$platform')({
  /**
   * Two-phase guard: (1) reject unknown platform params, (2) enforce TRIAL_POC
   * contract — prevents LIVE-only subsidiaries from entering the trial funnel
   * for a platform they don't have a trial for.
   */
  beforeLoad: async ({ params }) => {
    if (!(VALID_PLATFORMS as string[]).includes(params.platform)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/access-denied' });
    }
    await requireContract(params.platform as TrialPlatform, ['TRIAL_POC'])();
  },
  component: TrialPlatformLayout,
});
